/**
 * Centralized enums for consistent data representation across the application
 * 
 * This file consolidates all enum values used throughout the application,
 * building on existing structures like roles.ts while adding new enums.
 */

import { ROLE_DEFINITIONS } from './roles';
import type { UserRole } from '../types/Auth';

// Re-export existing role enums for centralized access
export const APP_ENUMS = {
  // User-related enums (built on existing roles.ts)
  userRoles: Object.fromEntries(
    Object.entries(ROLE_DEFINITIONS).map(([key, value]) => [key.toUpperCase(), value.role])
  ) as Record<string, UserRole>,
  
  userStatus: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    PENDING: 'pending',
    SUSPENDED: 'suspended',
  },
  
  // Organization-related enums
  organizationTypes: {
    INSURER: 'insurer',
    REINSURER: 'reinsurer',
    BROKER: 'broker',
    AGENT: 'agent',
  },
  
  organizationStatus: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    PENDING_APPROVAL: 'pending_approval',
  },
  
  // Product-related enums (for future use)
  productTypes: {
    CROP: 'crop',
    WEATHER: 'weather',
    PARAMETRIC: 'parametric',
    LIVESTOCK: 'livestock',
  },
  
  productStatus: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    DRAFT: 'draft',
    ARCHIVED: 'archived',
  },
  
  // Policy-related enums (for future use)
  policyStatus: {
    DRAFT: 'draft',
    ACTIVE: 'active',
    EXPIRED: 'payment_collection_expired',
    CANCELLED: 'cancelled',
    CLAIMED: 'claimed',
  },
  
  // Quote-related enums (for future use)
  quoteStatus: {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    EXPIRED: 'payment_collection_expired',
  },
  
  // Claims-related enums (for future use)
  claimStatus: {
    SUBMITTED: 'submitted',
    UNDER_REVIEW: 'under_review',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    PAID: 'paid',
  },
  
  // UI-related enums
  badgeVariants: {
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
    INFO: 'info',
    SECONDARY: 'secondary',
  },
  
  buttonIntents: {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    OUTLINE: 'outline',
    DANGER: 'danger',
    GHOST: 'ghost',
    LINK: 'link',
  },
  
  // Table-specific enums
  columnTypes: {
    TEXT: 'text',
    DATE: 'date',
    CURRENCY: 'currency',
    STATUS: 'status',
    AVATAR: 'avatar',
    ACTIONS: 'actions',
  },
  
  // Priority/Severity enums
  priority: {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical',
  },
  
} as const;

// Type extraction for TypeScript support
export type UserStatus = typeof APP_ENUMS.userStatus[keyof typeof APP_ENUMS.userStatus];
export type OrganizationType = typeof APP_ENUMS.organizationTypes[keyof typeof APP_ENUMS.organizationTypes];
export type OrganizationStatus = typeof APP_ENUMS.organizationStatus[keyof typeof APP_ENUMS.organizationStatus];
export type ProductType = typeof APP_ENUMS.productTypes[keyof typeof APP_ENUMS.productTypes];
export type ProductStatus = typeof APP_ENUMS.productStatus[keyof typeof APP_ENUMS.productStatus];
export type PolicyStatus = typeof APP_ENUMS.policyStatus[keyof typeof APP_ENUMS.policyStatus];
export type QuoteStatus = typeof APP_ENUMS.quoteStatus[keyof typeof APP_ENUMS.quoteStatus];
export type ClaimStatus = typeof APP_ENUMS.claimStatus[keyof typeof APP_ENUMS.claimStatus];
export type BadgeVariant = typeof APP_ENUMS.badgeVariants[keyof typeof APP_ENUMS.badgeVariants];
export type ButtonIntent = typeof APP_ENUMS.buttonIntents[keyof typeof APP_ENUMS.buttonIntents];
export type ColumnType = typeof APP_ENUMS.columnTypes[keyof typeof APP_ENUMS.columnTypes];
export type Priority = typeof APP_ENUMS.priority[keyof typeof APP_ENUMS.priority];

// Helper to get all values from an enum
export const getEnumValues = <T extends Record<string, string>>(enumObj: T): T[keyof T][] => {
  return Object.values(enumObj) as T[keyof T][];
};

// Helper to get all keys from an enum
export const getEnumKeys = <T extends Record<string, string>>(enumObj: T): (keyof T)[] => {
  return Object.keys(enumObj) as (keyof T)[];
}; 